"use client";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  Sidebar,
  useSidebar,
} from "@/components/ui";
import { menuSectionsTop, menuSectionsBottom } from "@/config/navigation";
import { filterMenuSectionsByClaims } from "@/lib/claims/menu-filter";
import { Icon } from "@iconify/react";
import { usePathname } from "next/navigation";
import NextLink from "next/link";
import { Logo } from "./Logo";
import { SIDEBAR_COLLAPSIBLE_MODE } from "./constants";
import { useActiveProject } from "@/context/active-project";
import { useActiveOrganization } from "@/context/active-organization";
import { useTranslate } from "@tolgee/react";
import { menuClaimsForScopedProjects } from "@/lib/auth/session-menu-claims";
import { useSession } from "next-auth/react";
import clsx from "clsx";
function NavSidebarItem({
  exactMatch = false,
  href,
  icon,
  label,
}: {
  exactMatch?: boolean;
  href: string | null;
  icon: string;
  label: string;
}) {
  const pathname = usePathname();
  const { isMobile, setOpenMobile } = useSidebar();
  const current =
    href !== null &&
    (exactMatch ? pathname === href : pathname === href || pathname.startsWith(href + "/"));
  const isExternal = href?.startsWith("http");
  const isGradient = href?.startsWith("https://www.infoplaza.com");
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={current}
        tooltip={label}
        disabled={href === null}
        onClick={() => isMobile && setOpenMobile(false)}
        className={clsx(
          "data-active:bg-sidebar-primary data-active:text-sidebar-primary-foreground data-active:hover:bg-sidebar-primary data-active:hover:text-sidebar-primary-foreground",
          isGradient &&
            "bg-gradient-to-r from-[#FF9900] to-[#00BF78] bg-clip-text text-transparent!",
        )}
        render={
          href === null ? undefined : (
            <NextLink href={href} target={isExternal ? "_blank" : undefined} />
          )
        }
      >
        <Icon icon={icon} className={clsx(isGradient && "text-[#FF9900]!")} />
        <span>{label}</span>
        {isExternal && (
          <Icon
            icon="lucide:chevron-right"
            className={clsx("ml-auto size-3!", isGradient && "text-[#00BF78]!")}
          />
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
export function Menu() {
  const { data: session } = useSession();
  const { organizations, activeOrganization, activeOrganizationId, setActiveOrganizationId } =
    useActiveOrganization();
  const { projects, activeProjectId, setActiveProjectId } = useActiveProject();
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isExpanded = isMobile || state === "expanded";
  const { t } = useTranslate();
  const menuClaims = menuClaimsForScopedProjects(session ?? null, activeProjectId, projects);
  const sectionsTop = filterMenuSectionsByClaims(menuSectionsTop, menuClaims);
  const sectionsBottom = filterMenuSectionsByClaims(menuSectionsBottom, menuClaims);
  const noOrganization = organizations.length === 0;
  const singleProjectInScope = projects.length === 1;
  const showOrganizationSwitcher = organizations.length > 1;
  /** When the org has one project and org is not picked in a separate switcher, show org name here. */
  const showSingleProjectScopeLabel =
    singleProjectInScope && !showOrganizationSwitcher && activeOrganization;
  return (
    <Sidebar collapsible={SIDEBAR_COLLAPSIBLE_MODE}>
      <SidebarHeader className="flex-row items-center justify-between whitespace-nowrap">
        <NextLink href="/dashboard" className="dark:hidden">
          <Logo iconOnly={!isExpanded} />
        </NextLink>
        <NextLink href="/dashboard" className="hidden dark:block">
          <Logo iconOnly={!isExpanded} white={true} />
        </NextLink>
        {!isMobile && (
          <Button variant="ghost" size="icon" className="rounded-full" onClick={toggleSidebar}>
            <Icon
              icon={state === "collapsed" ? "lucide:panel-left-open" : "lucide:panel-left-close"}
            />
          </Button>
        )}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("nav.closenavigation")}
            onClick={() => setOpenMobile(false)}
          >
            <Icon icon="lucide:x" />
          </Button>
        )}
      </SidebarHeader>
      <SidebarContent>
        {isExpanded && showOrganizationSwitcher && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("menu.organization")}</SidebarGroupLabel>
            <Select
              value={activeOrganizationId ?? organizations[0]?.id ?? ""}
              onValueChange={(id) => {
                if (id) {
                  setActiveOrganizationId(id);
                }
              }}
            >
              <SelectTrigger
                aria-label={t("menu.activeorganization")}
                className="w-full max-w-full min-w-0 bg-card"
              >
                <SelectValue>
                  {(id: string) => (
                    <>
                      <Icon icon="lucide:users" />
                      {organizations.find((o) => o.id === id)?.name}
                    </>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {organizations.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <Icon icon="lucide:users" />
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SidebarGroup>
        )}
        {!noOrganization && isExpanded && (projects.length > 1 || showSingleProjectScopeLabel) && (
          <SidebarGroup>
            <SidebarGroupLabel>
              {singleProjectInScope && !showOrganizationSwitcher
                ? t("menu.organization")
                : t("menu.project")}
            </SidebarGroupLabel>
            {showSingleProjectScopeLabel ? (
              <span className="flex min-w-0 items-center gap-1.5 rounded-md border border-sidebar-border px-3 py-2 text-xs font-medium">
                <Icon
                  icon={projects[0].themeIcon}
                  className="size-3.5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="truncate">{activeOrganization.name}</span>
              </span>
            ) : (
              <Select
                value={activeProjectId ?? projects[0]?.id ?? ""}
                onValueChange={(id) => {
                  if (id) {
                    setActiveProjectId(id);
                  }
                }}
              >
                <SelectTrigger
                  aria-label={t("menu.activeproject")}
                  className="w-full max-w-full min-w-0 bg-card"
                >
                  <SelectValue>
                    {(id: string) => {
                      const project = projects.find((p) => p.id === id);
                      return (
                        project && (
                          <>
                            <Icon icon={project.themeIcon} />
                            {project.name}
                          </>
                        )
                      );
                    }}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <Icon icon={p.themeIcon} />
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </SidebarGroup>
        )}
        {!noOrganization &&
          sectionsTop.map((section, index) => (
            <SidebarGroup key={section.labelKey + index.toString()}>
              {section.labelKey && <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>}
              <SidebarMenu>
                {section.items.map((item) => (
                  <NavSidebarItem
                    key={item.href + item.labelKey}
                    exactMatch={item.exactMatch}
                    href={item.href}
                    icon={item.icon}
                    label={item.label ?? t(item.labelKey)}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
      </SidebarContent>
      {!noOrganization && !isMobile && (
        <SidebarFooter>
          <SidebarSeparator />
          {sectionsBottom.map((section, index) => (
            <SidebarGroup key={section.labelKey + index.toString()}>
              {section.labelKey && <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>}
              <SidebarMenu>
                {section.items.map((item) => (
                  <NavSidebarItem
                    key={item.href + item.labelKey}
                    exactMatch={item.exactMatch}
                    href={item.href}
                    icon={item.icon}
                    label={item.label ?? t(item.labelKey)}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
